import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Button } from '@kawai/ui';
import type { TenantCardMeta } from '@kawai/shared';
import {
  loadPayjp,
  getPayjpPublicKey,
  tokenizeCardWith3ds,
  type PayjpInstance,
  type PayjpCardElement,
} from '../lib/payjp.js';
import {
  callBillingRegisterCard,
  billingRegisterCardErrorMessage,
} from '../lib/api/billingRegisterCard.js';

interface CardRegistrationFormProps {
  /** 登録対象テナント（自テナント）。 */
  tenantId: string;
  /** 登録成功時に新しい既定カードのメタを通知する。 */
  onRegistered?: (card: TenantCardMeta) => void;
}

/**
 * TP-20: payjp.js v2 + 3D セキュアでカードをトークン化し、`billingRegisterCard` を呼ぶフォーム。
 * カード番号は payjp.js が直接トークン化し、当アプリ／サーバーには渡らない（非保持化）。
 * tenant-portal は React Query 非使用のため、送信状態は useState で持つ。
 * S-136: 見た目は v2（カード入力欄は灰面 / 枠線なし / 文字 7 段）。
 */
/** payjp.js v2 の card element を mount する div の id（CSS セレクタ #... で参照）。 */
const CARD_MOUNT_ID = 'payjp-card-element';

export function CardRegistrationForm({
  tenantId,
  onRegistered,
}: CardRegistrationFormProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const payjpRef = useRef<PayjpInstance | null>(null);
  const cardElRef = useRef<PayjpCardElement | null>(null);
  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let cardEl: PayjpCardElement | null = null;
    void (async () => {
      try {
        const Payjp = await loadPayjp();
        if (cancelled) return;
        const payjp = Payjp(getPayjpPublicKey());
        payjpRef.current = payjp;
        cardEl = payjp.elements().create('card');
        cardElRef.current = cardEl;
        // payjp.js v2 の mount は CSS セレクタ文字列を取る（DOM 要素の直渡しは不可）。
        // div に付けた固定 id をセレクタとして渡す。useEffect 実行時には div は描画済み。
        if (mountRef.current) cardEl.mount(`#${CARD_MOUNT_ID}`);
        setReady(true);
      } catch (e) {
        if (!cancelled) {
          setLoadError(
            e instanceof Error ? e.message : 'payjp.js の初期化に失敗しました',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
      try {
        cardEl?.unmount();
      } catch {
        // unmount 失敗は無視（再描画時の二重 unmount 等）。
      }
    };
  }, []);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const payjp = payjpRef.current;
    const cardEl = cardElRef.current;
    if (!payjp || !cardEl) {
      setError('カードフォームが初期化されていません');
      return;
    }
    setSubmitting(true);
    try {
      const token = await tokenizeCardWith3ds(payjp, cardEl);
      const res = await callBillingRegisterCard({ tenantId, token: token.id });
      onRegistered?.(res.card);
    } catch (err) {
      setError(billingRegisterCardErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return <p className={ERR}>カード入力欄を読み込めませんでした：{loadError}</p>;
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="flex max-w-[420px] flex-col gap-3">
      <label className="text-label font-medium text-ink-2">カード情報（3Dセキュア対応）</label>
      {/* payjp.js の iframe が入る面。白カードの上なので灰面 */}
      <div
        id={CARD_MOUNT_ID}
        ref={mountRef}
        className="min-h-11 rounded-lg bg-surface-2 px-3.5 py-3"
      />
      {error && <p className={ERR}>{error}</p>}
      <Button type="submit" disabled={!ready || submitting} className="self-start">
        {submitting ? '登録中…' : 'カードを登録する'}
      </Button>
      <p className="text-label text-ink-3">
        カード番号は当社サーバーに送信されず、PAY.JP で安全にトークン化されます。
      </p>
    </form>
  );
}

const ERR = 'text-label text-danger-ink';
